import React, {useRef, useEffect, useState, useCallback} from 'react'
import {Box, TextField, IconButton, Tooltip, Chip, CircularProgress} from '@mui/material'
import {
  Send as SendIcon,
  Stop as StopIcon,
  AddCircleOutline as AttachIcon,
  Mic as MicIcon,
  StopCircle as StopRecordingIcon,
  CheckCircle as CheckIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material'

interface PendingFile {
  name: string
  id?: string
  uploading: boolean
}

interface MessageInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop?: () => void
  onAttach?: (files: File[]) => void
  onMic?: () => void
  disabled?: boolean
  isStreaming?: boolean
  isRecording?: boolean
  isVoiceConnecting?: boolean
  voiceTranscript?: string
  pendingFiles?: PendingFile[]
}

const ACCEPTED_EXTENSIONS = '.docx,.xlsx,.csv,.tsv,.pdf,.pptx,.xls,.doc,.jpg,.jpeg,.png,.gif,.svg,.webp,.mp4,.webm,.mov,.html,.htm,.yaml,.yml,.txt'

const MessageInput: React.FC<MessageInputProps> = ({
  value,
  onChange,
  onSend,
  onStop,
  onAttach,
  onMic,
  disabled,
  isStreaming,
  isRecording,
  isVoiceConnecting,
  voiceTranscript,
  pendingFiles = []
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus()
    }
  }, [disabled])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const hasText = value.trim().length > 0
      const hasFiles = pendingFiles.some(f => f.id)
      if (!disabled && (hasText || hasFiles)) {
        onSend()
      }
    }
  }

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || !onAttach) return
    onAttach(Array.from(files))
  }, [onAttach])

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!onAttach) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [onAttach])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (!onAttach) return
    handleFileSelect(e.dataTransfer.files)
  }, [onAttach, handleFileSelect])

  const attachEnabled = !!onAttach

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'relative'
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'primary.main',
            opacity: 0.08,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* File chips */}
      {pendingFiles.length > 0 && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1}}>
          {pendingFiles.map((f, i) => (
            <Chip
              key={i}
              icon={f.uploading ? <CircularProgress size={14} /> : (f.id ? <CheckIcon fontSize="small" color="success" /> : <FileIcon fontSize="small" />)}
              label={f.name}
              size="small"
              variant="outlined"
              sx={{maxWidth: 200}}
            />
          ))}
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS}
        style={{display: 'none'}}
        onChange={e => handleFileSelect(e.target.files)}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.5,
          border: 1,
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 3,
          px: 1,
          py: 0.5,
          bgcolor: 'background.default',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.12)'
          },
          transition: 'border-color 0.15s, box-shadow 0.15s'
        }}
      >
        {/* Left actions */}
        <Tooltip title={attachEnabled ? 'Attach file' : 'Attach file (coming soon)'}>
          <span>
            <IconButton
              size="small"
              onClick={attachEnabled ? handleAttachClick : undefined}
              disabled={!attachEnabled}
              sx={{color: attachEnabled ? 'text.secondary' : 'text.disabled', mb: 0.25}}
            >
              <AttachIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isRecording ? 'Stop recording' : isVoiceConnecting ? 'Connecting...' : onMic ? 'Voice input' : 'Voice input (coming soon)'}>
          <span>
            <IconButton
              size="small"
              onClick={onMic}
              disabled={!onMic || isVoiceConnecting || disabled}
              sx={{
                mb: 0.25,
                color: isRecording ? 'error.main' : onMic ? 'text.secondary' : 'text.disabled',
                ...(isRecording && {
                  animation: 'pulse-recording 1.5s ease-in-out infinite',
                  '@keyframes pulse-recording': {
                    '0%, 100%': {opacity: 1},
                    '50%': {opacity: 0.5}
                  }
                })
              }}
            >
              {isRecording ? <StopRecordingIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        {/* Text field */}
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isRecording}
          placeholder={isRecording && voiceTranscript ? voiceTranscript : isRecording ? 'Listening...' : 'Ask Ros anything... (Shift+Enter for new line)'}
          variant="standard"
          InputProps={{disableUnderline: true}}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              lineHeight: 1.6,
              py: 0.5,
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
            },
            '& .MuiInputBase-input': {
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: '0.875rem'
            }
          }}
        />

        {/* Send / Stop button */}
        {isStreaming && onStop ? (
          <Tooltip title="Stop generating">
            <IconButton
              size="small"
              color="error"
              onClick={onStop}
              sx={{mb: 0.25}}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Send (Enter)">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={onSend}
                disabled={disabled || (!value.trim() && pendingFiles.filter(f => f.id).length === 0)}
                sx={{mb: 0.25}}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

export default MessageInput
